import { api } from './Api';

const nativeId = (id) => typeof id === 'string' && /^[1-9][0-9]*$/.test(id);
const uuid = (id) => typeof id === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
const text = (s, max = 256) => typeof s === 'string' && s.length > 0 &&
  Array.from(s).length <= max && !/[\u0000-\u001f\u007f-\u009f]/.test(s);
const timestamp = (s) => typeof s === 'string' && s.endsWith('Z') && Number.isFinite(Date.parse(s));
const malformed = () => {
  const err = new Error('The tablet service returned an invalid response. Refresh manually or contact support.');
  err.code = 'INVALID_RESPONSE';
  err.recovery_action = 'none';
  throw err;
};
const ensure = (condition) => { if (!condition) malformed(); };
const pick = (record, keys) => Object.fromEntries(keys.map((key) => [key, record[key]]));

export const createOperationId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) {
    throw new Error('Secure randomness is unavailable. Use a secure, supported browser.');
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Strict projection: never consume arbitrary account properties or infer labels.
const display = (value, restaurant = false) => {
  ensure(value && nativeId(value.id));
  const labelKey = restaurant ? 'name' : 'display_name';
  const label = value[labelKey];
  ensure(['current', 'retained', 'unavailable'].includes(value.display_status));
  if (value.display_status === 'unavailable') {
    ensure(label === null && value.display_source === null && value.display_observed_at === null);
  } else {
    ensure(text(label, 128) && label.trim() === label &&
      value.display_source === 'eatzy_api' && timestamp(value.display_observed_at));
  }
  if (!restaurant) ensure([true, false, null].includes(value.app_user));
  return pick(value, ['id', labelKey, ...(!restaurant ? ['app_user'] : []),
    'display_status', 'display_source', 'display_observed_at']);
};

const boundRestaurant = (value, id) => {
  const result = display(value, true);
  ensure(result.id === String(id));
  return result;
};
const actor = (value, issuer = false) => {
  if (value === null) return null;
  ensure(value && (issuer ? value.type === 'human' :
    ['human', 'device', 'service'].includes(value.type)) &&
    (value.type === 'human' ? nativeId(value.id) : text(value.id)));
  return pick(value, ['type', 'id']);
};
const device = (value, restId) => {
  ensure(value && text(value.id) && value.restaurant_id === String(restId));
  ensure(['active', 'revoked'].includes(value.enrollment_status) &&
    ['allowed', 'blocked', 'unknown'].includes(value.access_state));
  ensure(typeof value.display_name === 'string' && nativeId(value.issuer_id) &&
    ['android', 'ios'].includes(value.platform) && timestamp(value.created_at));
  ensure(value.last_seen_at === null || timestamp(value.last_seen_at));
  ensure(value.access_checked_at === null || timestamp(value.access_checked_at));
  ensure(value.access_reason === null || text(value.access_reason));
  ensure(value.revoked_at === null || timestamp(value.revoked_at));
  ensure(value.revoked_reason === null || text(value.revoked_reason));
  ensure(typeof value.app_version === 'string' && typeof value.os_version === 'string');
  if (value.access_state === 'allowed') ensure(value.enrollment_status === 'active' &&
    value.access_reason === null && timestamp(value.access_checked_at));
  if (value.enrollment_status === 'revoked') ensure(value.access_state === 'blocked' && timestamp(value.revoked_at));
  return { ...pick(value, ['id', 'restaurant_id', 'issuer_id', 'display_name', 'platform',
    'app_version', 'os_version', 'enrollment_status', 'access_state', 'access_reason',
    'access_checked_at', 'created_at', 'last_seen_at', 'revoked_at', 'revoked_reason']),
  restaurant: boundRestaurant(value.restaurant, restId), associated_user: display(value.associated_user) };
};
const pairing = (value, restId, issue, userId) => {
  ensure(value && uuid(value.pairing_id) && ['pending', 'redeemed', 'expired', 'canceled'].includes(value.state) &&
    timestamp(value.expires_at));
  const user = display(value.associated_user);
  if (userId) ensure(user.id === userId);
  ensure(value.state === 'redeemed' ? text(value.device_id) : value.device_id === null);
  const result = {
    ...pick(value, ['pairing_id', 'state', 'expires_at', 'device_id']),
    restaurant: boundRestaurant(value.restaurant, restId), associated_user: user,
  };
  if (issue) {
    ensure(typeof value.recovered === 'boolean' && typeof value.display_name === 'string');
    ensure(value.state === 'pending' ? /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/.test(value.code) : value.code === null);
    return { ...result, ...pick(value, ['display_name', 'recovered', 'code']) };
  }
  ensure(value.restaurant_id === String(restId) && value.associated_user_id === user.id &&
    nativeId(value.issuer_id) && timestamp(value.created_at));
  ensure(value.state === 'redeemed' ? timestamp(value.redeemed_at) : value.redeemed_at === null);
  ensure(value.canceled_at === null || timestamp(value.canceled_at));
  return { ...result, ...pick(value, ['restaurant_id', 'associated_user_id', 'issuer_id',
    'created_at', 'redeemed_at', 'canceled_at']) };
};
const audit = (value, restId) => {
  ensure(value && ['eatzy_api', 'auth_hub'].includes(value.source) && text(value.id) &&
    value.restaurant_id === String(restId) &&
    timestamp(value.occurred_at) && ['allowed', 'denied', 'completed', 'failed'].includes(value.outcome));
  const eventActor = actor(value.event_actor);
  const issuer = actor(value.pairing_issuer, true);
  ensure(value.issuer_id === (issuer?.id || null));
  ensure(text(value.action) && text(value.request_id) &&
    (value.reason === null || text(value.reason)) &&
    (value.associated_user_id === null || nativeId(value.associated_user_id)) &&
    (value.device_id === null || text(value.device_id)) &&
    (value.pairing_id === null || uuid(value.pairing_id)));
  return { ...pick(value, ['id', 'source', 'restaurant_id', 'issuer_id', 'associated_user_id',
    'device_id', 'pairing_id', 'action', 'outcome', 'reason', 'occurred_at', 'request_id']),
  event_actor: eventActor, pairing_issuer: issuer };
};
const page = (value, project) => {
  ensure(value && Array.isArray(value.items) &&
    (value.next_cursor === null || text(value.next_cursor, 4096)));
  return { items: value.items.map(project), next_cursor: value.next_cursor };
};

const header = (headers, name) => headers?.get?.(name) || headers?.[name.toLowerCase()];
export const safePairingError = (raw) => {
  const body = raw?.error || raw;
  const err = new Error(text(body?.message, 1000) ? body.message : 'Tablet management is unavailable. Retry this operation manually.');
  err.status = Number.isInteger(raw?.status) ? raw.status : undefined;
  err.code = /^[A-Z_]{1,80}$/.test(body?.code) ? body.code : 'REQUEST_FAILED';
  err.recovery_action = /^[a-z_]{1,80}$/.test(body?.recovery_action) ? body.recovery_action : 'none';
  const requestId = header(raw?.responseHeaders, 'X-Request-ID') || body?.request_id;
  err.request_id = text(requestId, 256) ? requestId : undefined;
  err.device_id = text(body?.device_id) ? body.device_id : undefined;
  const retryAfter = Number(header(raw?.responseHeaders, 'Retry-After'));
  err.retryAfter = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined;
  err.retryable = body?.retryable === true;
  return err;
};
const request = async (run, project) => {
  try { return project(await run()); } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw safePairingError(err);
  }
};
const path = (restId) => {
  if (!nativeId(String(restId))) throw new Error('A valid restaurant is required.');
  return `/v1/restaurants/${restId}/tablet-pairing`;
};
const options = (opts, key) => ({
  signal: opts?.signal, noAutoRetry: true,
  ...(key ? { headers: { 'Idempotency-Key': key } } : {}),
});
const params = (cursor) => ({ limit: 50, ...(cursor ? { cursor } : {}) });
const validName = (name) => text(name, 64) && name.trim() === name;
export const listUsers = (id, cursor, opts) => request(
  () => api.get(`${path(id)}/users`, params(cursor), options(opts)),
  (value) => page(value, (user) => display(user)));
export const issueCode = (id, body, key, opts) => {
  if (!nativeId(body?.associated_user_id) || !validName(body?.display_name) || !uuid(key)) {
    return Promise.reject(new Error('Choose a user and a tablet nickname of 1–64 characters.'));
  }
  const original = pick(body, ['associated_user_id', 'display_name']);
  return request(() => api.post(`${path(id)}/codes`, original, options(opts, key)),
    (value) => {
      ensure(value?.display_name === original.display_name);
      return pairing(value, id, true, original.associated_user_id);
    });
};
export const getStatus = (id, pairingId, opts) => request(
  () => api.get(`${path(id)}/codes/${encodeURIComponent(pairingId)}`, undefined, options(opts)),
  (value) => { ensure(value?.pairing_id === pairingId); return pairing(value, id, false); });
export const cancelCode = (id, pairingId, opts) => request(
  () => api.delete(`${path(id)}/codes/${encodeURIComponent(pairingId)}`, undefined, undefined, options(opts)),
  () => null);
export const listDevices = (id, cursor, opts) => request(
  () => api.get(`${path(id)}/devices`, params(cursor), options(opts)),
  (value) => page(value, (row) => device(row, id)));
export const renameDevice = (id, deviceId, displayName, opts) => {
  if (!validName(displayName)) return Promise.reject(new Error('Enter a tablet nickname of 1–64 characters.'));
  return request(() => api.patch(`${path(id)}/devices/${encodeURIComponent(deviceId)}`,
    { display_name: displayName }, options(opts)),
  (value) => { ensure(value?.id === deviceId); return device(value, id); });
};
export const revokeDevice = (id, deviceId, reason, key, opts) => {
  if (!uuid(key) || !['administrator_requested', 'lost_device', 'security_response', 'replaced_device'].includes(reason)) {
    return Promise.reject(new Error('Choose a supported revocation reason.'));
  }
  return request(() => api.post(`${path(id)}/devices/${encodeURIComponent(deviceId)}/revoke`,
    { reason }, options(opts, key)),
  (value) => { ensure(value?.id === deviceId); return device(value, id); });
};
export const listAudit = (id, cursor, opts) => request(
  () => api.get(`${path(id)}/audit`, params(cursor), options(opts)),
  (value) => page(value, (row) => audit(row, id)));

const storageKey = (scope) => {
  if (!text(scope, 512)) throw new Error('An authenticated recovery scope is required.');
  return `tablet-pairing:pending:${scope}`;
};
// sessionStorage survives reloads, not browser sign-out/new login bindings.
// Explicit allowlisting prevents the returned code/response entering storage.
export const pendingOperations = {
  read(scope) {
    let value;
    try { value = JSON.parse(window.sessionStorage.getItem(storageKey(scope))); } catch (_) { return null; }
    if (!value || !uuid(value.key) || !nativeId(value.body?.associated_user_id) ||
        !validName(value.body?.display_name) || !Number.isFinite(value.createdAt) ||
        value.createdAt > Date.now() || Date.now() - value.createdAt > 90 * 86400000) return null;
    return { key: value.key, body: pick(value.body, ['associated_user_id', 'display_name']), createdAt: value.createdAt };
  },
  write(scope, value) {
    if (!uuid(value?.key) || !nativeId(value.body?.associated_user_id) ||
        !validName(value.body?.display_name) || !Number.isFinite(value.createdAt)) {
      throw new Error('Invalid pending tablet operation.');
    }
    try {
      window.sessionStorage.setItem(storageKey(scope), JSON.stringify({
        key: value.key, body: pick(value.body, ['associated_user_id', 'display_name']), createdAt: value.createdAt,
      }));
    } catch (_) {
      throw new Error('Recovery storage is unavailable. Enable session storage before issuing a code.');
    }
  },
  clear(scope) {
    try { window.sessionStorage.removeItem(storageKey(scope)); } catch (_) { /* No secret is stored. */ }
  },
};

const revocationStorageKey = (scope, deviceId) => {
  storageKey(scope); // Validate the same authenticated human/session/restaurant scope.
  if (!text(deviceId)) throw new Error('A device is required for revocation recovery.');
  return `tablet-pairing:revocation:${JSON.stringify([scope, deviceId])}`;
};
const validRevocation = (value) => uuid(value?.key) &&
  ['administrator_requested', 'lost_device', 'security_response', 'replaced_device'].includes(value.reason);
const revocationStorageError = () => new Error(
  'Revocation recovery storage is unavailable or invalid. Restore session storage before retrying; no replacement operation was created.'
);

// A permanent action needs durable, scoped recovery too. Never reset an
// unreadable record, expire an unresolved operation, or generate a retry key.
export const revocationOperations = {
  read(scope, deviceId) {
    try {
      const raw = window.sessionStorage.getItem(revocationStorageKey(scope, deviceId));
      if (raw === null) return null;
      const value = JSON.parse(raw);
      if (!validRevocation(value)) throw revocationStorageError();
      return pick(value, ['key', 'reason']);
    } catch (_) {
      throw revocationStorageError();
    }
  },
  write(scope, deviceId, value) {
    if (!validRevocation(value)) throw revocationStorageError();
    try {
      window.sessionStorage.setItem(revocationStorageKey(scope, deviceId),
        JSON.stringify(pick(value, ['key', 'reason'])));
    } catch (_) {
      throw revocationStorageError();
    }
  },
  clear(scope, deviceId) {
    try {
      window.sessionStorage.removeItem(revocationStorageKey(scope, deviceId));
    } catch (_) {
      throw revocationStorageError();
    }
  },
};
